// pictionary/games/ImageToWordGame.js
import React, { useEffect, useState, useRef, useCallback } from 'react';
import {
  Box,
  Typography,
  Card,
  CardMedia,
  Paper,
  ButtonBase,
  Grow,
  Fade,
  Avatar,
  useTheme,
} from '@mui/material';
import { useGame } from '../../../context/GameContext';
import { green, red, grey, blue } from '@mui/material/colors';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CancelIcon from '@mui/icons-material/Cancel';

const ImageToWordGame = ({ word, onAnswerSubmit, topic }) => {
  const { shuffleArray } = useGame();
  const theme = useTheme();
  const [options, setOptions] = useState([]);
  const [selectedOptionId, setSelectedOptionId] = useState(null);
  const [selectionStatus, setSelectionStatus] = useState(null);
  const [isAnswered, setIsAnswered] = useState(false);

  const wordIdRef = useRef('');

  // Reset state when the word changes
  useEffect(() => {
    if (word && word.id !== wordIdRef.current) {
      console.log('New word detected, resetting state for:', word.word);
      setSelectedOptionId(null);
      setSelectionStatus(null);
      setIsAnswered(false);
      wordIdRef.current = word.id;

      // Option Generation Logic
      if (topic) {
        const generateOptions = () => {
          const opts = [word];
          const availableWords = topic.words.filter((w) => w.id !== word.id);
          const shuffledWrong = shuffleArray([...availableWords]);
          for (let i = 0; i < Math.min(3, shuffledWrong.length); i++) {
            opts.push(shuffledWrong[i]);
          }
          return shuffleArray(opts);
        };
        setOptions(generateOptions());
      } else {
        setOptions([]);
      }
    } else if (!word) {
      setOptions([]);
      setSelectedOptionId(null);
      setSelectionStatus(null);
      setIsAnswered(false);
      wordIdRef.current = '';
    }
  }, [word, topic, shuffleArray]);

  // Memoize the answer handling function
  const handleAnswer = useCallback((selectedId) => {
    if (isAnswered || !word) return;

    const isCorrect = selectedId === word.id;
    setSelectedOptionId(selectedId);
    setSelectionStatus(isCorrect ? 'correct' : 'incorrect');
    setIsAnswered(true);

    // Call the parent callback function
    onAnswerSubmit(isCorrect, word.word);
  }, [isAnswered, word, onAnswerSubmit]);

  // Handle keyboard shortcuts
  useEffect(() => {
    const handleKeyPress = (e) => {
      // Number keys for selecting options
      const keyNum = parseInt(e.key, 10);
      if (!isNaN(keyNum) && keyNum >= 1 && keyNum <= options.length) {
        const index = keyNum - 1;
        if (options[index] && !isAnswered) {
          handleAnswer(options[index].id);
        }
      }

      // Enter key for continuing after answering
      if (e.key === 'Enter' && isAnswered) {
        // This would trigger the same effect as pressing continue
        // The parent component will handle the actual continuation
      }
    };

    document.addEventListener('keydown', handleKeyPress);
    return () => {
      document.removeEventListener('keydown', handleKeyPress);
    };
  }, [options, handleAnswer, isAnswered]);

  if (!word) return null;

  return (
    <Box
      sx={{
        width: '100%',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'space-between',
        height: '100%',
        p: 3,
      }}
    >
      {/* Image Display - SIGNIFICANTLY LARGER */}
      <Grow in={true} timeout={500}>
        <Card
          elevation={8} // Stronger shadow for 3D effect
          sx={{
            width: { xs: '80%', sm: 380, md: 420 }, // Much larger, responsive
            height: { xs: 260, sm: 320, md: 380 }, // Much taller
            mb: 5, // More space below
            mt: 3,
            cursor: 'default',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            p: 3, // More internal padding
            borderRadius: 3,
            // overflow: 'hidden',
            position: 'relative',
            // Duolingo-style 3D effect
            boxShadow: '0 12px 24px rgba(0,0,0,0.2), 0 8px 12px rgba(0,0,0,0.1)',
            transform: 'translateY(-5px)', // Subtle lift
            '&::after': { // Bottom "layer" for 3D effect
              content: '""',
              position: 'absolute',
              bottom: -8,
              left: 4,
              right: 4,
              height: 8,
              backgroundColor: 'rgba(0,0,0,0.15)',
              borderBottomLeftRadius: 3,
              borderBottomRightRadius: 3,
              zIndex: -1,
            }
          }}
        >
          <CardMedia
            component="img"
            image={word.image}
            alt={`Image for ${word.word}`}
            sx={{
              maxWidth: '92%',
              maxHeight: '92%',
              objectFit: 'contain',
              borderRadius: 2,
            }}
          />
        </Card>
      </Grow>

      {/* Options Area - HORIZONTAL ROW LAYOUT */}
      <Box
        sx={{
          display: 'flex',
          flexDirection: { xs: 'column', sm: 'row' }, // Row on larger screens, column on mobile
          justifyContent: 'center',
          alignItems: 'center',
          gap: 3, // Good spacing between options
          width: '100%',
          maxWidth: 900,
          // overflow: 'auto', // Allow horizontal scrolling if needed
          py: 2, // Padding top/bottom
        }}
      >
        {options.map((option, index) => {
          const isSelected = selectedOptionId === option.id;
          const isCorrectSelection = isSelected && selectionStatus === 'correct';
          const isIncorrectSelection = isSelected && selectionStatus === 'incorrect';

          // Duolingo-style card with 3D effect
          return (
            <Grow
              key={option.id}
              in={true}
              timeout={500}
              style={{ transitionDelay: `${index * 100}ms` }}
            >
              <Box
                sx={{
                  position: 'relative',
                  minWidth: { xs: '85%', sm: '160px', md: '200px' },
                  flex: { sm: '1 0 auto' },
                  // Add bottom "layer" for 3D effect
                  '&::after': {
                    content: '""',
                    position: 'absolute',
                    bottom: 0,
                    left: 5,
                    right: 5,
                    height: 8,
                    backgroundColor: 'rgba(0,0,0,0.15)',
                    borderBottomLeftRadius: 8,
                    borderBottomRightRadius: 8,
                    zIndex: 0,
                    transition: 'all 0.2s ease',
                  },
                  // When selected, change the 3D effect
                  ...(isSelected && {
                    '&::after': {
                      backgroundColor: isCorrectSelection ? green[300] : red[300],
                      bottom: -6, // Reduced distance to show "pressing" effect
                    }
                  })
                }}
              >
                <ButtonBase
                  onClick={() => handleAnswer(option.id)}
                  disabled={isAnswered}
                  sx={{
                    width: '100%',
                    display: 'block',
                    textAlign: 'left',
                    borderRadius: 2,
                  }}
                >
                  <Paper
                    elevation={isSelected ? 2 : 8} // Lower elevation when selected to simulate pressing
                    sx={{
                      p: 3, // Increased padding
                      minHeight: { xs: '80px', sm: '100px' }, // Taller cards
                      borderRadius: 2,
                      textAlign: 'center',
                      position: 'relative',
                      border: '3px solid',
                      borderColor: isSelected 
                        ? isCorrectSelection ? green[500] : red[500]
                        : grey[300],
                      backgroundColor: isSelected
                        ? isCorrectSelection ? green[50] : red[50]
                        : grey[50],
                      transition: 'all 0.3s ease',
                      transform: isSelected ? 'translateY(2px)' : 'translateY(-5px)', // 3D press effect
                      boxShadow: isSelected 
                        ? 'none' 
                        : '0 8px 16px rgba(0,0,0,0.15), 0 2px 4px rgba(0,0,0,0.12)',
                      
                      // Hover effect only if not answered
                      ...(!isAnswered && {
                        '&:hover': {
                          borderColor: blue[300],
                          backgroundColor: blue[50],
                          transform: 'translateY(-8px)',
                          boxShadow: '0 12px 20px rgba(0,0,0,0.2), 0 4px 8px rgba(0,0,0,0.1)',
                        }
                      })
                    }}
                  >
                    {/* Option Text */}
                    <Typography 
                      variant="h5" 
                      component="span"
                      sx={{
                        fontWeight: 'bold',
                        color: isSelected
                          ? isCorrectSelection ? green[900] : red[900]
                          : grey[800],
                      }}
                    >
                      {option.word}
                    </Typography>

                    {/* Feedback Icon Area */}
                    {isSelected && (
                      <Fade in={true} timeout={300}>
                        <Avatar
                          sx={{
                            position: 'absolute',
                            top: '50%',
                            right: 16,
                            transform: 'translateY(-50%)',
                            width: 36,
                            height: 36,
                            backgroundColor: isCorrectSelection ? green[600] : red[600],
                            boxShadow: '0 2px 8px rgba(0,0,0,0.2)',
                          }}
                        >
                          {isCorrectSelection ? (
                            <CheckCircleIcon sx={{ color: 'white', fontSize: '1.5rem' }} />
                          ) : (
                            <CancelIcon sx={{ color: 'white', fontSize: '1.5rem' }} />
                          )}
                        </Avatar>
                      </Fade>
                    )}

                    {/* Keyboard Shortcut Number Badge */}
                    {!isAnswered && (
                      <Box
                        sx={{
                          position: 'absolute',
                          top: 8,
                          left: 8,
                          backgroundColor: 'rgba(0,0,0,0.6)',
                          color: 'white',
                          fontSize: '0.75rem',
                          fontWeight: 'bold',
                          p: '3px 8px',
                          borderRadius: '8px',
                          lineHeight: 1,
                          boxShadow: '0 2px 4px rgba(0,0,0,0.15)',
                        }}
                      >
                        {index + 1}
                      </Box>
                    )}
                  </Paper>
                </ButtonBase>
              </Box>
            </Grow>
          );
        })}
      </Box>
    </Box>
  );
};

export default ImageToWordGame;